import { CompositeScreenProps } from '@react-navigation/native'
import { useTheme } from 'contexts'
import React, { useMemo } from 'react'
import { StyleSheet } from 'react-native'
import { Bar, BarChart, Cell, LabelList, ResponsiveContainer, YAxis } from 'recharts'

import { Page } from 'partials/Page'

import { Box } from 'widgets/Box'
import { Separator } from 'widgets/Separator'
import { Text } from 'widgets/Text'

import { useCurrentUser } from 'hooks/useCurrentUser'
import { useUser } from 'hooks/useUser'

import { UpscorePageFooter } from './UpscorePageFooter'

const barColors = ['#FB426E', '#4578F1', '#B9519B', '#8160C0']

const getDynamicPath = (x, y, width, height) =>
  `m ${width / 2 + x} 350
  c -18 0 -34 -16 -34 -36
  c 0 -${height} 0 -${height} 0 -${height} l 68 -33
  v ${height + 32}
  c 0 20 -16 36 -34 36 
  z`

const DynamicBar = ({ fill, x, y, width, height }) => {
  return <path d={getDynamicPath(x, y, width, height)} stroke='none' fill={fill} />
}

const getBackgroundPath = (x, y, width, height) =>
  `M ${width / 2 + x} 350
  c -18 0 -34 -16 -34 -36 l 0 -220 c 0 -6 4 -14 12 -18 l 56 -26 v 264 c 0 19 -16 36 -34 36 z`

const BackgroundBar = ({ fill, x, y, width, height }) => {
  return <path d={getBackgroundPath(x, y, width, height)} stroke='none' fill={fill} />
}

export const UpscorePage = ({ navigation, route }: CompositeScreenProps<any, any>) => {
  const theme = useTheme()

  const userId = route.params?.userId
  const { data: user } = useUser({ userId })
  const { data: currentUser } = useCurrentUser()
  const isCurrentUser = useMemo(() => userId === currentUser?.id, [currentUser?.id, userId])

  const data = useMemo(
    () => [
      {
        name: 'Workout',
        value: user?.upscore?.workout ?? 0,
        possible: user?.upscore?.total ?? 70,
      },
      { name: 'Sport', value: user?.upscore?.sport ?? 0, possible: user?.upscore?.total ?? 70 },
      {
        name: 'Recovery',
        value: user?.upscore?.recovery ?? 0,
        possible: user?.upscore?.total ?? 70,
      },
      {
        name: 'Mindfulness',
        value: user?.upscore?.mindfulness ?? 0,
        possible: user?.upscore?.total ?? 70,
      },
    ],
    [
      user?.upscore?.mindfulness,
      user?.upscore?.recovery,
      user?.upscore?.sport,
      user?.upscore?.total,
      user?.upscore?.workout,
    ],
  )

  const styles = StyleSheet.create({
    bottomLabelList: { fontWeight: 'bold', fontFamily: 'Poppins', fill: theme.colors.black },
    middleLabelList: {
      textAnchor: 'middle',
      fontWeight: 'bold',
      fontSize: 20,
      fontFamily: 'Poppins',
      fill: 'white',
    },
    pointsText: { marginTop: -24 },
  })

  if (!user) return null

  const customInnerLabel = ({ x, y, width, height, value }) => {
    return (
      <text
        x={x + width / 2}
        dy={330}
        fill='white'
        textAnchor='middle'
        fontFamily='Poppins'
        fontWeight='bold'
      >
        {value}
      </text>
    )
  }

  const customOuterLabel = ({ x, y, width, height, value }) => {
    return (
      <text
        x={x + width / 2}
        dy={380}
        fill='black'
        textAnchor='middle'
        fontFamily='Poppins'
        fontWeight='bold'
      >
        {value}
      </text>
    )
  }

  return (
    <Page
      withNav
      navigation={navigation}
      backgroundColor='white'
      title={isCurrentUser ? 'Your Upscore' : `${user.firstName}'s Upscore`}
      withBack
    >
      <Box align='center'>
        <Text color='black' fontSize='xxxl' bold black style={styles.pointsText}>
          {user.upscore?.total ?? 0}
        </Text>
        <Text color='v2midGrey' fontSize='md' bold style={styles.pointsText}>
          Points
        </Text>
      </Box>
      <Box flex={2.5} margin={{ top: -24 }}>
        <ResponsiveContainer width='100%' height={400}>
          <BarChart
            data={data}
            margin={{
              bottom: 32,
            }}
          >
            <YAxis type='number' domain={[0, user.upscore?.total ?? 70]} hide />
            <Bar
              dataKey='possible'
              stackId='a'
              fill={theme.colors.v2midGrey}
              isAnimationActive={false}
              shape={BackgroundBar}
            />
            <Bar
              dataKey='value'
              stackId='a'
              isAnimationActive={false}
              minPointSize={15}
              shape={DynamicBar}
            >
              <LabelList dataKey='value' position='bottom' content={customInnerLabel} />
              {data.map((entry, index) => (
                <Cell key={`${entry.name}-bar`} fill={barColors[index % 20]} />
              ))}
              <LabelList dataKey='name' position='bottom' content={customOuterLabel} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
      <Separator />
      <Box testID='UpscorePageFooterContainer' flex={1} padding='md'>
        <UpscorePageFooter />
      </Box>
    </Page>
  )
}
